<script setup lang="ts">
import InputGroup from '@/components/Auths/InputGroup.vue'
import PageHeader from '@/components/Globals/PageHeader.vue'
import MultiSelect from '@/components/Forms/MultiSelect.vue'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import yup from '@/plugins/yup'
import router from '@/router'
import { useGroupStore } from '@/store/group'
import { useSensorStore } from '@/store/sensor'
import { useCompanyStore } from '@/store/company'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { ValidationError } from 'yup'

const { t } = useI18n()

interface IMultiSelectOption {
  id: number
  value: number
  text: string
  selected: boolean
}

const isLoading = ref(true)
const route = useRoute()

const sensorMultiSelectRef = ref<InstanceType<typeof MultiSelect> | null>(null)
const shiftMultiSelectRef = ref<InstanceType<typeof MultiSelect> | null>(null)

const handlerClear = () => {
  sensorMultiSelectRef.value?.clearSelection()
  shiftMultiSelectRef.value?.clearSelection()
}
const errors = ref({
  name: '',
  sensors: '',
})
const sensorIdsList = ref<number[]>([])
const shiftIdsList = ref<number[]>([])
const storeGroup = useGroupStore()
const storeSensor = useSensorStore()
const companyStore = useCompanyStore()
const sensors = computed(() => storeSensor.sensors)
const shifts = computed(() => companyStore.shifts)
const fetchSensors = storeSensor.fetch

const formData = ref({
  name: '',
  sensors: '',
  description: '',
})

const handleSelectedValues = (values: Array<number | string>) => {
  sensorIdsList.value = values.map(Number)
}

const handleSelectedShifts = (values: Array<number | string>) => {
  shiftIdsList.value = values.map(Number)
}

const sensorOptions = computed<IMultiSelectOption[]>(() => {
  return sensors.value.map((sensor) => ({
    id: sensor.id,
    value: sensor.id,
    text: sensor.name,
    selected: sensorIdsList.value.includes(sensor.id),
  }))
})

const shiftOptions = computed<IMultiSelectOption[]>(() => {
  return shifts.value.map((shift) => ({
    id: shift.id,
    value: shift.id,
    text: shift.name,
    selected: shiftIdsList.value.includes(shift.id),
  }))
})

onMounted(() => {
  formData.value.name = route.query.name?.toString() || ''

  Promise.all([fetchSensors(), companyStore.fetchCompanyShifts()]).then(() => {
    isLoading.value = false
  })
})

const schema = yup.object().shape({
  name: yup.string().required(t('errors.required', { label: t('groups.name') })),
  sensors: yup
    .array()
    .of(yup.number())
    .min(1, t('errors.must_have', { label: t('sensors.name'), count: '1' })),
})

const saveGroup = async () => {
  isLoading.value = true

  try {
    await storeGroup.createGroup({
      name: formData.value.name,
      observation: formData.value.description,
      sensor_ids: sensorIdsList.value,
      shift_ids: shiftIdsList.value,
    })

    formData.value.name = ''
    formData.value.description = ''
    sensorIdsList.value = []
    shiftIdsList.value = []
    handlerClear()
  } finally {
    isLoading.value = false
  }
}

function handleSubmit() {
  schema
    .validate(
      {
        name: formData.value.name,
        sensors: sensorIdsList.value,
      },
      { abortEarly: false },
    )
    .then(() => {
      saveGroup()
    })
    .catch((err: ValidationError) => {
      setErrors(err)
    })
}

function setErrors(validationErrors: ValidationError) {
  errors.value = {
    name: '',
    sensors: '',
  }
  validationErrors.inner.forEach((error) => {
    if (error.path) {
      errors.value[error.path as keyof typeof errors.value] = error.message
    }
  })
}

const cancel = () => {
  router.back()
}
</script>

<template>
  <DefaultLayout>
    <Loader v-if="isLoading" />

    <div v-else class="mx-auto w-full max-w-[1400px] px-4 py-6">
      <PageHeader
        use-back
        title="Criar grupo"
        subtitle="Organize seus sensores em grupos para facilitar a gestão." />
      <div class="col-span-5 xl:col-span-3">
          <div class="border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div class="border-b border-stroke px-7 py-4 dark:border-strokedark">
              <h3 class="font-regular text-black dark:text-white">Com grupos de sensores você pode organizar seus sensores da melhor forma possível.</h3>
            </div>
            <div class="p-7">
              <form @submit.prevent="handleSubmit">
                <InputGroup v-model="formData.name" label="Nome" type="text" placeholder="Refrigeração" :errorText="errors.name" />

                <div class="mb-5.5">
                  <label class="text-md mb-3 block font-medium text-black dark:text-white" for="Username">Sensores</label>
                  <MultiSelect
                    :options="sensorOptions"
                    :errorText="errors.sensors"
                    ref="sensorMultiSelectRef"
                    @update:selectedValues="handleSelectedValues"
                    @clearSelection="handleSelectedValues([])" />
                </div>

                <div class="mb-5.5">
                  <label class="text-md mb-3 block font-medium text-black dark:text-white" for="shifts">
                    {{ t('groups.shifts') }}
                  </label>
                  <MultiSelect
                    ref="shiftMultiSelectRef"
                    :options="shiftOptions"
                    @update:selectedValues="handleSelectedShifts"
                    @clearSelection="handleSelectedShifts([])" />
                </div>

                <div class="mb-5.5">
                  <label class="text-md mb-3 block font-medium text-black dark:text-white" for="bio">Descrição</label>
                  <div class="relative">
                    <span class="absolute left-4.5 top-4">
                      <svg class="fill-current" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g opacity="0.8" clip-path="url(#clip0_88_10224)">
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M1.56524 3.23223C2.03408 2.76339 2.66997 2.5 3.33301 2.5H9.16634C9.62658 2.5 9.99967 2.8731 9.99967 3.33333C9.99967 3.79357 9.62658 4.16667 9.16634 4.16667H3.33301C3.11199 4.16667 2.90003 4.25446 2.74375 4.41074C2.58747 4.56702 2.49967 4.77899 2.49967 5V16.6667C2.49967 16.8877 2.58747 17.0996 2.74375 17.2559C2.90003 17.4122 3.11199 17.5 3.33301 17.5H14.9997C15.2207 17.5 15.4326 17.4122 15.5889 17.2559C15.7452 17.0996 15.833 16.8877 15.833 16.6667V10.8333C15.833 10.3731 16.2061 10 16.6663 10C17.1266 10 17.4997 10.3731 17.4997 10.8333V16.6667C17.4997 17.3297 17.2363 17.9656 16.7674 18.4344C16.2986 18.9033 15.6627 19.1667 14.9997 19.1667H3.33301C2.66997 19.1667 2.03408 18.9033 1.56524 18.4344C1.0964 17.9656 0.833008 17.3297 0.833008 16.6667V5C0.833008 4.33696 1.0964 3.70107 1.56524 3.23223Z"
                            fill="" />
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M16.6664 2.39884C16.4185 2.39884 16.1809 2.49729 16.0056 2.67253L8.25216 10.426L7.81167 12.188L9.57365 11.7475L17.3271 3.99402C17.5023 3.81878 17.6008 3.5811 17.6008 3.33328C17.6008 3.08545 17.5023 2.84777 17.3271 2.67253C17.1519 2.49729 16.9142 2.39884 16.6664 2.39884ZM14.8271 1.49402C15.3149 1.00622 15.9765 0.732178 16.6664 0.732178C17.3562 0.732178 18.0178 1.00622 18.5056 1.49402C18.9934 1.98182 19.2675 2.64342 19.2675 3.33328C19.2675 4.02313 18.9934 4.68473 18.5056 5.17253L10.5889 13.0892C10.4821 13.196 10.3483 13.2718 10.2018 13.3084L6.86847 14.1417C6.58449 14.2127 6.28409 14.1295 6.0771 13.9225C5.87012 13.7156 5.78691 13.4151 5.85791 13.1312L6.69124 9.79783C6.72787 9.65131 6.80364 9.51749 6.91044 9.41069L14.8271 1.49402Z"
                            fill="" />
                        </g>
                        <defs>
                          <clipPath id="clip0_88_10224">
                            <rect width="20" height="20" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </span>
                    <textarea
                      v-model="formData.description"
                      class="w-full border border-stroke bg-white py-3 pl-11.5 pr-4.5 font-normal text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary"
                      name="bio"
                      id="bio"
                      rows="6"
                      placeholder="Escreva sua descrição aqui"></textarea>
                  </div>
                </div>

                <!-- Save and Cancel Buttons -->
                <div class="flex justify-end gap-4.5">
                  <button
                    class="flex justify-center border border-stroke px-6 py-2 font-medium text-black hover:shadow-1 dark:border-strokedark dark:text-white"
                    type="button"
                    @click="cancel">
                    Cancelar
                  </button>
                  <button class="flex w-20 justify-center bg-primary px-6 py-2 font-medium text-white hover:bg-opacity-90" type="submit">
                    <div v-if="isLoading" class="animate-spin items-center">
                      <svg class="h-7 w-7 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <span v-else class="flex items-center justify-center">Salvar</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
    </div>
  </DefaultLayout>
</template>
